import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Radio, message } from "antd";
import { AudioOutlined, LoadingOutlined } from "@ant-design/icons";
import axios from "axios";
import { useTranslation } from "react-i18next";

// --- helpers ---
const glue = (a = "", b = "") => {
    if (!a) return b.trim();
    if (!b) return a;
    return /\s$/.test(a) ? a + b.trim() : a + " " + b.trim();
};

const BarStack = ({ bars = 5, level = 0, side = "left", activeColor = "rgba(255,255,255,0.95)" }) => {
    const active = Math.round((level / 100) * bars);
    const seq = Array.from({ length: bars }, (_, i) => i);
    const order = side === "left" ? [...seq].reverse() : seq;
    return (
        <div style={{ display: "flex", justifyContent: side === "left" ? "flex-end" : "flex-start", alignItems: "flex-end", width: 48, height: 16, gap: 3 }}>
            {order.map((i) => (
                <div key={i} style={{ width: 4, height: 4 + i * 2, borderRadius: 2, background: i < active ? activeColor : "rgba(255,255,255,0.25)", transition: "background 60ms linear" }} />
            ))}
        </div>
    );
};

export default function MicRecorder({ baseUrl = "", getInitialText, onUpdate, size = "middle", defaultLang = "ru-RU" }) {
    const { t } = useTranslation();
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    const useMeters = !isMobile;

    // --- UI state ---
    const [recording, setRecording] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [waitingResponse, setWaitingResponse] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);
    const [level, setLevel] = useState(0);
    const [lang, setLang] = useState(defaultLang);
    const [langModal, setLangModal] = useState(false);

    // --- refs ---
    const recognitionRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);
    const prefixRef = useRef("");
    const lastHandledRef = useRef(0);
    const activeRef = useRef(false);
    const stoppingRef = useRef(false);
    const cooldownRef = useRef(false);

    const isServerMode = lang === "whisper-backend";

    // --- meters ---
    const startMeters = async (src) => {
        try {
            const stream = src || (await navigator.mediaDevices.getUserMedia({ audio: true }));
            const Ctx = window.AudioContext || window.webkitAudioContext;
            audioCtxRef.current = new Ctx();
            const source = audioCtxRef.current.createMediaStreamSource(stream);
            analyserRef.current = audioCtxRef.current.createAnalyser();
            analyserRef.current.fftSize = 256;
            source.connect(analyserRef.current);
            const data = new Uint8Array(analyserRef.current.frequencyBinCount);
            const loop = () => {
                const an = analyserRef.current;
                if (!an) return;
                an.getByteTimeDomainData(data);
                let sum = 0;
                for (let i = 0; i < data.length; i++) {
                    const v = (data[i] - 128) / 128;
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / data.length);
                setLevel(Math.min(100, Math.round(rms * 140)));
                rafRef.current = requestAnimationFrame(loop);
            };
            loop();
        } catch {}
    };

    const stopMeters = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        try { analyserRef.current?.disconnect?.(); } catch {}
        analyserRef.current = null;
        try { audioCtxRef.current?.close?.(); } catch {}
        audioCtxRef.current = null;
    };

    // --- SpeechRecognition per start() to avoid 'aborted' ---
    const makeRecognizer = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) return null;
        const rec = new SR();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = lang === "whisper-backend" ? defaultLang : lang;

        rec.onresult = (e) => {
            if (!activeRef.current) return;

            if (isAndroid) {
                let lastFinal = "";
                for (let i = 0; i < e.results.length; i++) if (e.results[i].isFinal) lastFinal = (e.results[i][0]?.transcript || "").trim();
                if (!lastFinal) return;
                const prev = prefixRef.current;
                let delta = lastFinal.startsWith(prev) ? lastFinal.slice(prev.length).trim() : lastFinal;
                if (delta) {
                    prefixRef.current = glue(prev, delta);
                    onUpdate?.(prefixRef.current);
                }
                return;
            }

            const results = e.results;
            const startIndex = Math.max(lastHandledRef.current, e.resultIndex);
            let finalDelta = "";
            let interim = "";

            for (let i = startIndex; i < results.length; i++) {
                const r = results[i];
                const text = (r[0]?.transcript || "").trim();
                if (r.isFinal) finalDelta = glue(finalDelta, text); else interim = glue(interim, text);
            }

            if (finalDelta) {
                prefixRef.current = glue(prefixRef.current, finalDelta);
                lastHandledRef.current = results.length;
            } else {
                lastHandledRef.current = Math.max(lastHandledRef.current, startIndex);
            }

            const live = interim ? glue(prefixRef.current, interim) : prefixRef.current;
            onUpdate?.(live);
        };

        rec.onerror = (err) => {
            // Suppress 'aborted' and 'no-speech' when we intentionally stopped
            if (stoppingRef.current && (err?.error === "aborted" || err?.error === "no-speech")) return;
            message.error(t("postPage.micRecorder.messages.error", { error: err?.error || "unknown" }));
            setRecording(false);
            stopMeters();
        };

        rec.onend = () => {
            setRecording(false);
            stopMeters();
        };

        return rec;
    };

    const waitCooldown = () => new Promise((res) => setTimeout(res, 150));

    // --- recording control ---
    const start = async () => {
        try {
            if (cooldownRef.current) await waitCooldown();
            activeRef.current = true;
            stoppingRef.current = false;
            prefixRef.current = (getInitialText?.() || "").trim();
            if (prefixRef.current) onUpdate?.(prefixRef.current);
            lastHandledRef.current = 0;
            setRecording(true);

            if (isServerMode) {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                streamRef.current = stream;
                if (useMeters) setTimeout(() => startMeters(stream), 0);

                const MR = window.MediaRecorder || MediaRecorder;
                const mime = (MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus") && "audio/webm;codecs=opus") || (MediaRecorder.isTypeSupported?.("audio/webm") && "audio/webm") || undefined;
                const mr = mime ? new MR(stream, { mimeType: mime }) : new MR(stream);
                mediaRecorderRef.current = mr;
                chunksRef.current = [];
                mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
                mr.start(1000);
            } else {
                if (useMeters) setTimeout(() => startMeters(), 0);
                const rec = makeRecognizer();
                recognitionRef.current = rec;
                if (!rec) {
                    message.error(t("postPage.micRecorder.messages.unavailable"));
                    setRecording(false);
                    stopMeters();
                    return;
                }
                rec.lang = lang || defaultLang;
                rec.start();
            }
        } catch (e) {
            message.error(t("postPage.micRecorder.messages.startFail"));
            setRecording(false);
            stopMeters();
        }
    };

    const uploadAndTranscribe = async (blob) => {
        if (!blob) return;
        setUploading(true); setWaitingResponse(false); setUploadPct(0);
        try {
            const file = new File([blob], "recording.webm", { type: blob.type || "audio/webm" });
            const form = new FormData();
            form.append("audio", file);
            const resp = await axios.post(`${baseUrl}/upload-audio`, form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (ev) => {
                    if (!ev.total) return;
                    const pct = Math.round((ev.loaded / ev.total) * 100);
                    setUploadPct(pct);
                    if (pct >= 100) setTimeout(() => setWaitingResponse(true), 50);
                },
            });
            const text = (resp?.data?.text || "").trim();
            if (text) {
                prefixRef.current = glue(prefixRef.current, text);
                onUpdate?.(prefixRef.current);
            } else {
                message.warning(t("postPage.micRecorder.messages.emptyTrans") || "Пустая транскрипция");
            }
        } catch (e) {
            message.error(t("postPage.micRecorder.messages.error", { error: e?.message || "ASR failed" }));
        } finally {
            setUploading(false); setWaitingResponse(false); setUploadPct(0); chunksRef.current = [];
        }
    };

    const stop = () => {
        activeRef.current = false;
        stoppingRef.current = true;

        if (isServerMode) {
            try { mediaRecorderRef.current?.stop(); } catch {}
            try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
            streamRef.current = null;
            const blob = new Blob(chunksRef.current, { type: "audio/webm" });
            setRecording(false); lastHandledRef.current = 0;
            uploadAndTranscribe(blob);
        } else {
            try {
                const rec = recognitionRef.current;
                if (rec) {
                    rec.onresult = null; rec.onerror = null; rec.onend = null;
                    rec.abort?.();
                }
            } catch {}
            recognitionRef.current = null;
            setRecording(false); lastHandledRef.current = 0;
        }

        stopMeters();
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
        streamRef.current = null;
        try { audioCtxRef.current?.close?.(); } catch {}
        audioCtxRef.current = null;

        // small cooldown before next start to avoid 'aborted'
        cooldownRef.current = true;
        setTimeout(() => { cooldownRef.current = false; }, 150);
    };

    const toggle = () => {
        if (recording || uploading) { if (recording) stop(); return; }
        setLangModal(true);
    };

    useEffect(() => () => { try { stop(); } catch {} }, []);

    // --- center label ---
    const centerLabel = useMemo(() => {
        if (uploading && !waitingResponse) return (
            <>
                <LoadingOutlined style={{ fontSize: 16 }} />
                <span style={{ fontWeight: 400 }}>{(t("postPage.micRecorder.uploadingLabel") || "Загрузка") + "… " + uploadPct + "%"}</span>
            </>
        );
        if (uploading && waitingResponse) return (
            <>
                <LoadingOutlined style={{ fontSize: 16 }} />
                <span style={{ fontWeight: 400 }}>{t("postPage.micRecorder.processingLabel") || "Обработка"}…</span>
            </>
        );
        if (!recording) return (
            <>
                <AudioOutlined style={{ fontSize: 16 }} />
                <span style={{ fontWeight: 400 }}>{t("postPage.micRecorder.startLabel")}</span>
            </>
        );
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 4, background: "#fff" }}>
                <div style={{ width: 12, height: 12, borderRadius: 2, background: "#ff4d4f" }} />
            </div>
        );
    }, [uploading, waitingResponse, uploadPct, recording, t]);

    const recBg = recording ? "#ff4d4f" : undefined;

    return (
        <>
            <Button
                block
                size={size}
                type={recording ? "primary" : "default"}
                onClick={toggle}
                style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, background: recBg, overflow: "hidden" }}
                disabled={uploading}
            >
                <div style={{ display: "grid", gridTemplateColumns: "48px auto 48px", alignItems: "center", width: "100%", gap: 8 }}>
                    <div style={{ justifySelf: "end" }}>{recording ? <BarStack level={level} side="left" /> : <div style={{ width: 48, height: 16 }} />}</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 32, minWidth: 48, gap: 6 }}>{centerLabel}</div>
                    <div style={{ justifySelf: "start" }}>{recording ? <BarStack level={level} side="right" /> : <div style={{ width: 48, height: 16 }} />}</div>
                </div>
            </Button>

            <Modal
                title={t("postPage.micRecorder.chooseLang") || "Выберите язык"}
                open={langModal}
                onOk={() => { setLangModal(false); start(); }}
                onCancel={() => setLangModal(false)}
                okText={t("postPage.micRecorder.start") || "Начать"}
                cancelText={t("functions.close") || "Отмена"}
            >
                <Radio.Group value={lang} onChange={(e) => setLang(e.target.value)} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <Radio value="ru-RU">Русский</Radio>
                    <Radio value="kk-KZ">Қазақша</Radio>
                    <Radio value="en-US">English</Radio>
                    <Radio value="whisper-backend">AI Transcribe</Radio>
                </Radio.Group>
                <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
                    {t(lang === "whisper-backend" ? "postPage.micRecorder.serverModeHint" : "postPage.micRecorder.localModeHint")}
                </div>
            </Modal>
        </>
    );
}
